/**
 * Platinum Prop House - Custom Cart Logic
 * Handles: auto-add products, rush fee, distance calculator, fees, validation
 */

class PlatinumCart {
  constructor() {
    this.DEBUG_MODE = false;

    // Product IDs
    this.SHIPPING_PRODUCT_ID = 10943528388;
    this.SHIPPING_VARIANT_ID = 42072590024895;
    this.RUSH_FEE_PRODUCT_ID = 1417678749740;
    this.RUSH_FEE_VARIANT_ID = 42072588583103;
    this.LABOR_SURCHARGE_PRODUCT_ID = 7502199455935;
    this.LABOR_SURCHARGE_VARIANT_ID = 42380333056191;
    this.WORKERS_COMP_PRODUCT_ID = 7502199193791;
    this.WORKERS_COMP_VARIANT_ID = 42380333023423;

    // Upsell product IDs (products that don't count toward minimums)
    this.UPSELL_PRODUCT_IDS = [
      10944157700,
      405631795239,
      7442389303487,
      405631238183,
      7355895939263,
      7355895185599,
      7355894595775,
      8135335870655 // sandbag
    ];

    // Fee product IDs (shipping, rush fee, labor surcharge, workers comp)
    this.FEE_PRODUCT_IDS = [
      this.SHIPPING_PRODUCT_ID,
      this.RUSH_FEE_PRODUCT_ID,
      this.LABOR_SURCHARGE_PRODUCT_ID,
      this.WORKERS_COMP_PRODUCT_ID
    ];

    // Sandbag product ID (treated as add-on, not fee)
    this.SANDBAG_PRODUCT_ID = 8135335870655;

    this.init();
  }

  debug(...args) {
    if (this.DEBUG_MODE) {
      console.log('[PLATINUM CART]', ...args);
    }
  }

  logRushFeeDecision(message, data) {
    console.log('%c🚀 RUSH FEE: ' + message, 'color: #ff6b6b; font-weight: bold;', data || '');
  }

  /**
   * Intercept fetch calls to cart endpoints to detect deletions
   */
  interceptCartFetch() {
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);

      // Skip if we're in the middle of a rental date update, clearing uploads, or saving uploads
      if (sessionStorage.getItem('rentalDateUpdating') === 'true' ||
          sessionStorage.getItem('clearingUpload') === 'true' ||
          sessionStorage.getItem('savingUpload') === 'true') {
        return response;
      }

      // Check if this was a cart change/update call
      const url = args[0]?.toString() || '';
      if (url.includes('/cart/change') || url.includes('/cart/update') || url.includes('/cart/add')) {
        // Clone response to read it without consuming
        const clonedResponse = response.clone();

        try {
          const cartData = await clonedResponse.json();

          // Check if there are regular products left
          if (cartData.items) {
            self.cartData = cartData;

            // Broadcast cart change so validators/UI can refresh against live data
            document.dispatchEvent(new CustomEvent('platinum:cart-updated', {
              detail: { cart: cartData }
            }));

            const hasRegularProducts = cartData.items.some(item => {
              return !self.FEE_PRODUCT_IDS.includes(item.product_id) &&
                     !self.UPSELL_PRODUCT_IDS.includes(item.product_id);
            });

            if (!hasRegularProducts && cartData.items.length > 0) {
              self.debug('No regular products after cart update - scheduling cleanup');

              // Use setTimeout to let the current request complete first
              setTimeout(async () => {
                // Double-check flag hasn't been set in the meantime
                if (sessionStorage.getItem('rentalDateUpdating') !== 'true') {
                  await self.checkAndCleanupFees();
                }
              }, 100);
            }
          }
        } catch (e) {
          // Ignore JSON parse errors (might be section HTML response)
        }
      }

      return response;
    };
  }

  async init() {
    this.debug('Initializing Platinum Cart...');

    // Check if we just updated rental date - skip auto-processing to prevent loops
    this.skipAutoProcessing = sessionStorage.getItem('rentalDateUpdating') === 'true';
    if (this.skipAutoProcessing) {
      sessionStorage.removeItem('rentalDateUpdating');
      this.debug('Skipping auto-processing after rental date update');
    }

    // Setup global event listeners immediately (event delegation works even before DOM ready)
    this.setupGlobalEventListeners();

    // Wait for DOM for the rest
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Setup global event listeners using event delegation
   * These work regardless of when elements are added to the DOM
   */
  setupGlobalEventListeners() {
    // Intercept fetch calls to cart endpoints to detect when items are removed
    this.interceptCartFetch();

    // Listen for upsell checkbox changes (using event delegation for reliability)
    document.addEventListener('change', async (e) => {
      if (!e.target.classList.contains('optional-service-checkbox')) return;

      const checkbox = e.target;
      const variantId = checkbox.value || checkbox.dataset.variantId;

      if (!variantId) {
        console.error('No variant ID found for optional service checkbox');
        return;
      }

      this.debug('Optional service checkbox changed:', variantId, checkbox.checked);

      if (checkbox.checked) {
        await this.addToCart(variantId, 1);
      } else {
        await this.updateCartItem(variantId, 0);
      }

      window.location.reload();
    });

    // Listen for sandbag quantity button clicks
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.cart-addons__qty-btn');
      if (!btn) return;

      const variantId = btn.dataset.variantId;
      const action = btn.dataset.action;
      const container = btn.closest('.cart-addons__item--sandbag');
      const input = container?.querySelector('.cart-addons__qty-input');

      if (!variantId || !input) return;

      let currentQty = parseInt(input.value) || 0;
      let newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);

      this.debug('Sandbag quantity change:', { variantId, action, currentQty, newQty });

      input.value = newQty;
      btn.disabled = true;

      try {
        if (currentQty === 0 && newQty > 0) {
          // Adding to cart for the first time
          await this.addToCart(variantId, newQty);
        } else {
          // Updating existing cart item using /cart/update.js with updates object
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: { [variantId]: newQty } })
          });
        }
        window.location.reload();
      } catch (error) {
        console.error('Error updating sandbag quantity:', error);
        input.value = currentQty;
        btn.disabled = false;
      }
    });

    // Listen for sandbag quantity input changes
    document.addEventListener('change', async (e) => {
      if (!e.target.classList.contains('cart-addons__qty-input')) return;

      const input = e.target;
      const variantId = input.dataset.variantId;
      const originalQty = parseInt(input.dataset.originalQty) || 0;
      let newQty = parseInt(input.value) || 0;

      if (newQty < 0) newQty = 0;
      if (newQty > 99) newQty = 99;

      this.debug('Sandbag quantity input change:', { variantId, newQty });

      try {
        if (originalQty === 0 && newQty > 0) {
          // Adding to cart for the first time
          await this.addToCart(variantId, newQty);
        } else {
          // Updating existing cart item
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: { [variantId]: newQty } })
          });
        }
        window.location.reload();
      } catch (error) {
        console.error('Error updating sandbag quantity:', error);
      }
    });
  }

  async setup() {
    this.cartForm = document.querySelector('form.cart-form, form.cart');
    this.checkoutButton = document.querySelector('#checkout, button[name="checkout"]');

    if (!this.cartForm) {
      this.debug('Cart form not found - cart might be empty');
      return;
    }

    // Get cart data
    const cartData = await this.getCart();
    if (!cartData || cartData.item_count === 0) {
      this.debug('Cart is empty');
      return;
    }

    this.cartData = cartData;
    this.debug('Cart data:', cartData);

    // Check if there are regular products - if not, clean up fees and show empty cart
    const regularProducts = this.getRegularProducts();
    if (regularProducts.length === 0) {
      this.debug('No regular products found - cleaning up fee products');
      await this.cleanupFeeProducts();
      return;
    }

    // Detect cart type (pickup vs delivery)
    this.detectCartType();

    // Block auto-fee processing while any line item has a Rental Date in the past.
    // Past dates can't be fulfilled and would poison the rush-fee calculation —
    // ask the customer to update or remove those items first.
    const staleItems = this.getStaleRentalDateItems();
    if (staleItems.length > 0) {
      this.flagStaleRentalDateItems(staleItems);
      this.setupEventListeners();
      return;
    }

    // Handle auto-add products and rush fee together (skip if coming from rental date update)
    if (!this.skipAutoProcessing) {
      const needsReloadForProducts = await this.handleAutoAddProducts();
      const needsReloadForRushFee = await this.handleRushFee();

      // Only reload once after all products are added
      if (needsReloadForProducts || needsReloadForRushFee) {
        window.location.reload();
        return;
      }
    } else {
      this.debug('Skipping auto-processing due to rental date update');
    }

    // Setup event listeners
    this.setupEventListeners();
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Detect if cart is pickup only, mixed, or delivery only
   */
  detectCartType() {
    const pickupTag = 'Will Call';
    let hasPickup = false;
    let hasDelivery = false;

    this.cartData.items.forEach(item => {
      // Skip fee products
      if (this.FEE_PRODUCT_IDS.includes(item.product_id)) return;

      const tags = item.product?.tags || [];
      if (tags.includes(pickupTag)) {
        hasPickup = true;
      } else {
        hasDelivery = true;
      }
    });

    if (hasPickup && !hasDelivery) {
      this.cartType = 'pickup';
      document.body.classList.add('cart-pickup');
    } else if (hasPickup && hasDelivery) {
      this.cartType = 'mixed';
      document.body.classList.add('cart-mixed');
    } else {
      this.cartType = 'delivery';
      document.body.classList.add('cart-delivery');
    }

    this.debug('Cart type:', this.cartType);

    // Hide delivery-specific elements for pickup-only carts
    if (this.cartType === 'pickup') {
      document.querySelectorAll('.delivery-only').forEach(el => el.style.display = 'none');
    }
  }

  /**
   * Get regular products (excludes fees, upsells, shipping)
   */
  getRegularProducts(cartData) {
    const data = cartData || this.cartData;
    return data.items.filter(item => {
      return !this.FEE_PRODUCT_IDS.includes(item.product_id) &&
             !this.UPSELL_PRODUCT_IDS.includes(item.product_id);
    });
  }

  /**
   * Authoritative rental subtotal in cents from a cart payload.
   * Mirrors what gets saved as `_Rental Total`; checkout validation uses this.
   */
  getProductSubtotalCents(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return 0;
    return this.getRegularProducts(cartData).reduce((total, item) => total + item.line_price, 0);
  }

  /**
   * Calculate cart total from regular products only (uses this.cartData)
   */
  calculateProductTotal() {
    return this.getProductSubtotalCents(this.cartData);
  }

  /**
   * Check if a product is in the cart
   */
  isProductInCart(productId) {
    return this.cartData.items.some(item => item.product_id === productId);
  }

  /**
   * Add product to cart
   */
  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });

      if (!response.ok) throw new Error('Failed to add to cart');
      return await response.json();
    } catch (error) {
      console.error('Error adding to cart:', error);
      return null;
    }
  }

  /**
   * Update cart item quantity
   */
  async updateCartItem(variantId, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });

      if (!response.ok) throw new Error('Failed to update cart');
      return await response.json();
    } catch (error) {
      console.error('Error updating cart:', error);
      return null;
    }
  }

  /**
   * Remove all fee products when there are no regular products
   */
  async cleanupFeeProducts() {
    const updates = {};
    let hasFeeProducts = false;

    // Find all fee products and upsell products in cart
    for (const item of this.cartData.items) {
      if (this.FEE_PRODUCT_IDS.includes(item.product_id) || this.UPSELL_PRODUCT_IDS.includes(item.product_id)) {
        updates[item.key] = 0;
        hasFeeProducts = true;
      }
    }

    if (!hasFeeProducts) {
      this.debug('No fee products to clean up');
      return;
    }

    this.debug('Removing fee products:', updates);

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      // Reload to show empty cart
      window.location.reload();
    } catch (error) {
      console.error('Error cleaning up fee products:', error);
    }
  }

  /**
   * Check cart after update and cleanup fees if no regular products
   */
  async checkAndCleanupFees() {
    try {
      const cartData = await this.getCart();
      if (!cartData) return;

      // Check if there are any regular products
      const hasRegularProducts = cartData.items.some(item => {
        return !this.FEE_PRODUCT_IDS.includes(item.product_id) &&
               !this.UPSELL_PRODUCT_IDS.includes(item.product_id);
      });

      if (!hasRegularProducts && cartData.items.length > 0) {
        this.debug('No regular products left - cleaning up fees');

        // Build updates to remove all remaining items
        const updates = {};
        for (const item of cartData.items) {
          updates[item.key] = 0;
        }

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });

        // Reload to show empty cart
        window.location.reload();
      }
    } catch (error) {
      console.error('Error checking/cleaning fees:', error);
    }
  }

  /**
   * Handle auto-add products (shipping, labor surcharge, workers comp)
   * @returns {Promise<boolean>} Whether products were added and page needs reload
   */
  async handleAutoAddProducts() {
    // Skip for pickup-only carts
    if (this.cartType === 'pickup') {
      this.debug('Pickup cart - skipping auto-add products');
      return false;
    }

    const regularProducts = this.getRegularProducts();
    if (regularProducts.length === 0) {
      this.debug('No regular products - skipping auto-add');
      return false;
    }

    let needsReload = false;
    const productTotal = this.calculateProductTotal();

    // Add shipping product if not present
    if (!this.isProductInCart(this.SHIPPING_PRODUCT_ID)) {
      this.debug('Adding shipping product...');
      await this.addToCart(this.SHIPPING_VARIANT_ID, 1);
      needsReload = true;
    }

    // Add labor surcharge (10% of product total)
    const laborSurchargeQty = Math.ceil(productTotal / 100 * 0.1); // Convert cents to quantity
    if (!this.isProductInCart(this.LABOR_SURCHARGE_PRODUCT_ID)) {
      this.debug('Adding labor surcharge...', laborSurchargeQty);
      await this.addToCart(this.LABOR_SURCHARGE_VARIANT_ID, laborSurchargeQty);
      needsReload = true;
    }

    // Add workers comp (3.99% of product total)
    const workersCompQty = Math.ceil(productTotal / 100 * 0.0399);
    if (!this.isProductInCart(this.WORKERS_COMP_PRODUCT_ID)) {
      this.debug('Adding workers comp...', workersCompQty);
      await this.addToCart(this.WORKERS_COMP_VARIANT_ID, workersCompQty);
      needsReload = true;
    }

    return needsReload;
  }

  /**
   * Get rental date from cart items
   */
  getRentalDate() {
    // Return the EARLIEST (soonest) rental date across all non-fee items, so a
    // multi-date cart is judged by its most-rush item. Otherwise a far-out item
    // listed first could hide a fee that a later, imminent item should trigger.
    let earliestValue = null;
    let earliestTime = Infinity;

    for (const item of this.cartData.items) {
      // Skip fee products
      if (this.FEE_PRODUCT_IDS.includes(item.product_id)) continue;
      if (!item.properties) continue;

      // Use the first rental/event date property on this item
      for (const [key, value] of Object.entries(item.properties)) {
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes('rental date') || lowerKey.includes('event date')) {
          const parsed = this.getPacificDate(value);
          if (parsed && parsed.getTime() < earliestTime) {
            earliestTime = parsed.getTime();
            earliestValue = value;
          }
          break;
        }
      }
    }

    if (earliestValue) {
      this.debug('Earliest rental date across cart items:', earliestValue);
      return earliestValue;
    }

    // Fall back to the Event Date cart attribute when no item has a date
    const eventDateInput = document.querySelector('[name="attributes[Event Date]"]');
    if (eventDateInput && eventDateInput.value) {
      this.debug('Found rental date in cart attributes:', eventDateInput.value);
      return eventDateInput.value;
    }

    return null;
  }

  /**
   * Parse date in Pacific timezone
   */
  getPacificDate(dateString = null) {
    if (dateString) {
      // Handle both MM-DD-YYYY and YYYY-MM-DD formats
      let parts;
      if (dateString.includes('-')) {
        parts = dateString.split('-');
        // Detect format
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          return new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0));
        } else {
          // MM-DD-YYYY
          return new Date(Date.UTC(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]), 12, 0, 0));
        }
      }
      return null;
    } else {
      // Get current date in Pacific timezone
      const now = new Date();
      const pacificFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Los_Angeles',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      const parts = pacificFormatter.formatToParts(now);
      const year = parseInt(parts.find(p => p.type === 'year').value);
      const month = parseInt(parts.find(p => p.type === 'month').value);
      const day = parseInt(parts.find(p => p.type === 'day').value);

      return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    }
  }

  /**
   * Current Pacific date used for rush-fee counting, with a 6 PM cutoff:
   * orders placed at or after 6:00 PM Pacific count as the NEXT calendar day
   * (the business is closed for the day, so prep effectively starts tomorrow).
   * Returns a UTC-noon Date like getPacificDate(). Scoped to the rush fee only —
   * the delivery lead-time minimum keeps using getPacificDate() (plain date).
   * @returns {Date} today (or tomorrow, past cutoff) at UTC noon
   */
  getRushFeeOrderDate() {
    const base = this.getPacificDate();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hour: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date());
    const hourPart = parts.find(p => p.type === 'hour');
    const hour = hourPart ? parseInt(hourPart.value, 10) : 0;
    // At/after 18:00 Pacific, roll to the next calendar day. countBusinessDays
    // skips any weekend the roll lands on, so Fri-evening rolls past the weekend.
    if (hour >= 18) {
      base.setUTCDate(base.getUTCDate() + 1);
    }
    return base;
  }

  countBusinessDays(startDate, endDate) {
    if (endDate < startDate) return -1;
    // Count business days of lead time from the order day up to (but NOT
    // including) the event day: prep can't happen on the event day itself, so
    // it never counts. The order (placement) day counts as the 1st business
    // day. So the rush window is the 3 business days leading up to the event
    // (e.g. a Thursday event => Mon/Tue/Wed); an order placed in that window is
    // "within 3 business days". Same-day/next-day orders return 0 and charge.
    let count = 0;
    const current = new Date(startDate.getTime());
    while (current < endDate) {
      const day = current.getUTCDay();
      if (day !== 0 && day !== 6) count++;
      current.setUTCDate(current.getUTCDate() + 1);
    }
    return count;
  }

  /**
   * Find cart items whose Rental Date property is before today (Pacific).
   * @returns {Array<{key: string, title: string, rentalDate: string}>}
   */
  getStaleRentalDateItems() {
    const today = this.getPacificDate();
    if (!today) return [];

    const stale = [];
    for (const item of this.cartData.items) {
      if (this.FEE_PRODUCT_IDS.includes(item.product_id)) continue;
      if (!item.properties) continue;

      let rentalDateValue = null;
      for (const [key, value] of Object.entries(item.properties)) {
        if (key.toLowerCase().includes('rental date')) {
          rentalDateValue = value;
          break;
        }
      }
      if (!rentalDateValue) continue;

      const itemDate = this.getPacificDate(rentalDateValue);
      if (itemDate && itemDate < today) {
        stale.push({ key: item.key, title: item.title, rentalDate: rentalDateValue });
      }
    }
    return stale;
  }

  /**
   * Surface stale-dated items in the UI: highlight the rows, show a banner above
   * the cart explaining the issue, and disable checkout until the customer either
   * updates each rental date (existing inline edit UI) or removes the item.
   */
  flagStaleRentalDateItems(staleItems) {
    this.debug('Stale rental dates detected:', staleItems);

    // Highlight affected cart rows
    for (const item of staleItems) {
      const row = document.querySelector(`[data-key="${item.key}"]`);
      if (row) row.classList.add('cart-items__table-row--stale-date');
    }

    // Inject banner once
    if (!document.querySelector('.cart-stale-date-banner')) {
      const banner = document.createElement('div');
      banner.className = 'cart-stale-date-banner';
      banner.setAttribute('role', 'alert');

      const heading = document.createElement('strong');
      heading.textContent =
        staleItems.length === 1
          ? 'A rental date in your cart has passed.'
          : `${staleItems.length} rental dates in your cart have passed.`;
      banner.appendChild(heading);

      const body = document.createElement('p');
      body.textContent =
        'Please choose a new date for the highlighted item' +
        (staleItems.length === 1 ? '' : 's') +
        ' below (use the pencil icon next to the date), or remove ' +
        (staleItems.length === 1 ? 'it' : 'them') +
        ' from your cart. Checkout is disabled until this is resolved.';
      banner.appendChild(body);

      const list = document.createElement('ul');
      for (const item of staleItems) {
        const li = document.createElement('li');
        li.textContent = `${item.title} — was ${item.rentalDate}`;
        list.appendChild(li);
      }
      banner.appendChild(list);

      const wrapper = document.querySelector('.cart-items__wrapper') || this.cartForm;
      if (wrapper && wrapper.parentNode) {
        wrapper.parentNode.insertBefore(banner, wrapper);
      }
    }

    // Disable checkout
    document.querySelectorAll('#checkout, button[name="checkout"], [name="checkout"]').forEach((btn) => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('cart-stale-date-blocked');
    });
  }

  /**
   * Handle rush fee logic (add/remove based on delivery date)
   * @returns {Promise<boolean>} Whether rush fee was changed and page needs reload
   */
  async handleRushFee() {
    // Skip for pickup-only carts
    if (this.cartType === 'pickup') {
      this.debug('Pickup cart - skipping rush fee check');
      return false;
    }

    // Skip if there are no regular products - don't auto-manage fees for empty carts
    const regularProducts = this.getRegularProducts();
    if (regularProducts.length === 0) {
      this.debug('No regular products - skipping rush fee check');
      return false;
    }

    const rentalDate = this.getRentalDate();
    if (!rentalDate) {
      this.debug('No rental date found - removing rush fee if present');
      return await this.removeRushFee();
    }

    const deliveryDate = this.getPacificDate(rentalDate);
    // 6 PM cutoff: orders placed at/after 6:00 PM Pacific count as the next day.
    const currentDate = this.getRushFeeOrderDate();

    if (!deliveryDate || !currentDate) {
      this.debug('Could not parse dates');
      return await this.removeRushFee();
    }

    // Business days of lead time from the effective order date up to (not
    // including) the event day. Order day = 1; the event day never counts.
    const businessDays = this.countBusinessDays(currentDate, deliveryDate);

    this.debug('Business days of lead time before event:', businessDays);

    // Rush fee applies when the order is placed within the 3 business days
    // leading up to the event (0 = same/next-day; -1 = event already passed).
    const isRushDelivery = businessDays >= 0 && businessDays <= 3;

    if (isRushDelivery) {
      this.logRushFeeDecision('APPLIES - Delivery within 3 business days', {
        rentalDate,
        businessDaysUntilDelivery: businessDays
      });
      return await this.addRushFee();
    } else {
      this.logRushFeeDecision('DOES NOT APPLY - Delivery more than 3 business days away', {
        rentalDate,
        businessDaysUntilDelivery: businessDays
      });
      return await this.removeRushFee();
    }
  }

  /**
   * @returns {Promise<boolean>} Whether rush fee was added
   */
  async addRushFee() {
    if (this.isProductInCart(this.RUSH_FEE_PRODUCT_ID)) {
      this.debug('Rush fee already in cart');
      return false;
    }

    this.debug('Adding rush fee...');
    await this.addToCart(this.RUSH_FEE_VARIANT_ID, 1);
    return true;
  }

  /**
   * @returns {Promise<boolean>} Whether rush fee was removed
   */
  async removeRushFee() {
    // Find the rush fee line item by product ID
    const rushFeeItem = this.cartData.items.find(item => item.product_id === this.RUSH_FEE_PRODUCT_ID);

    if (!rushFeeItem) {
      this.debug('Rush fee not in cart - nothing to remove');
      return false;
    }

    this.debug('Removing rush fee...', { key: rushFeeItem.key });

    // Use the line item key for more reliable removal
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: rushFeeItem.key, quantity: 0 })
      });

      if (!response.ok) {
        throw new Error('Failed to remove rush fee');
      }

      return true;
    } catch (error) {
      console.error('Error removing rush fee:', error);
      return false;
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Listen for quantity changes to update fees. Use event delegation on document
    // so handlers survive section morphing when the cart re-renders.
    document.addEventListener('change', async (e) => {
      const target = e.target;
      if (!target.matches('.cart-items__quantity input, .quantity-selector input')) return;

      const cartData = await this.getCart();
      this.cartData = cartData;
      document.dispatchEvent(new CustomEvent('platinum:cart-updated', {
        detail: { cart: cartData }
      }));
      await this.updateFees();
    });
  }

  /**
   * Update fee quantities based on cart total
   */
  async updateFees() {
    const productTotal = this.calculateProductTotal();

    // Update labor surcharge (10%)
    const laborSurchargeQty = Math.ceil(productTotal / 100 * 0.1);
    await this.updateCartItem(this.LABOR_SURCHARGE_VARIANT_ID, laborSurchargeQty);

    // Update workers comp (3.99%)
    const workersCompQty = Math.ceil(productTotal / 100 * 0.0399);
    await this.updateCartItem(this.WORKERS_COMP_VARIANT_ID, workersCompQty);
  }

  /**
   * Calculate fee/add-on total (everything that isn't a rental product)
   */
  calculateFeeTotal() {
    return this.cartData.items
      .filter(item =>
        this.FEE_PRODUCT_IDS.includes(item.product_id) ||
        this.UPSELL_PRODUCT_IDS.includes(item.product_id)
      )
      .reduce((total, item) => total + item.line_price, 0);
  }

  /**
   * Save fee breakdown as hidden cart attributes before checkout
   * Prefixed with _ so they don't show in customer notifications
   */
  async saveFeeSummaryAttributes() {
    const rentalTotal = this.calculateProductTotal();
    const feeTotal = this.calculateFeeTotal();

    const rentalFormatted = '$' + (rentalTotal / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });
    const feeFormatted = '$' + (feeTotal / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            '_Rental Total': rentalFormatted,
            '_Fee Total': feeFormatted
          }
        })
      });
      this.debug('Saved fee summary attributes:', { rentalFormatted, feeFormatted });
    } catch (error) {
      console.error('Error saving fee summary attributes:', error);
    }
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.PlatinumCart = new PlatinumCart();
}
